import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, MaskLines } from "@/components/site/Reveal";
import { Seal } from "@/components/site/Seal";
import { Seo } from "@/components/site/Seo";
import { IMAGES, WHY_TWINS } from "@/lib/data";
const PageHero = ({ overline, title, subtitle, image }) => (
  <section className="relative pt-40 md:pt-52 pb-20 md:pb-28 overflow-hidden bg-maroon text-cream">
    <div className="absolute inset-0 -z-10 opacity-25">
      <img src={image} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-maroon/70" />
    </div>
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
        <span className="h-px w-8 bg-gold" />{overline}
      </motion.span>
      <h1 className="mt-6 font-display text-6xl md:text-8xl leading-[0.95] tracking-tight">
        <MaskLines lines={title} start={0.1} />
      </h1>
      {subtitle && (
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }} className="mt-8 text-cream/80 text-lg max-w-2xl leading-relaxed">
          {subtitle}
        </motion.p>
      )}
    </div>
  </section>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo title="About Us — Defining Brands Since 1997" description="Since 1997, Twins Media has helped businesses build stronger brands through strategic railway station and high-footfall outdoor advertising across India." path="/about" />
      <PageHero
        overline="About Twins Media"
        title={["Defining Brands", <span key="s" className="italic text-gold">Since 1997</span>]}
        subtitle="Helping businesses build stronger brands through innovative advertising across railway stations and high-footfall public locations."
        image={IMAGES.transitMedia}
      />

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7 space-y-6 text-charcoal-muted text-lg leading-relaxed">
            <Reveal><p>Since 1997, Twins Media has been helping businesses build stronger brands through innovative advertising solutions across railway stations and high-footfall public locations.</p></Reveal>
            <Reveal delay={0.05}><p>Our focus is simple — connect brands with people through strategic advertising placements that maximize visibility and customer engagement.</p></Reveal>
            <Reveal delay={0.1}><p>Whether you're promoting a local business or launching a nationwide campaign, our experienced team provides end-to-end support from planning and design to installation and campaign execution.</p></Reveal>
          </div>
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <Reveal delay={0.1}><Seal className="w-56 h-56 md:w-72 md:h-72" /></Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-paper" data-testid="why-twins">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="mb-14 max-w-2xl">
            <Reveal><span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-maroon"><span className="h-px w-8 bg-maroon" />Why Choose Twins Media?</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tight text-charcoal">Experience You Can Trust</h2></Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {WHY_TWINS.map((w, i) => (
              <Reveal key={w} delay={(i % 3) * 0.05}>
                <div className="group bg-cream border border-charcoal/10 p-8 md:p-10 h-full hover:bg-maroon hover:border-maroon transition-colors duration-400">
                  <span className="font-display text-4xl text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-5 font-display text-2xl md:text-3xl text-charcoal group-hover:text-cream transition-colors duration-400">{w}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="bg-maroon text-cream px-8 md:px-16 py-16 md:py-20 flex flex-col md:flex-row items-center justify-between gap-8">
              <h2 className="font-display text-4xl md:text-5xl leading-tight max-w-xl">Let's build your next <span className="italic text-gold">campaign</span>.</h2>
              <Link to="/contact" data-testid="about-cta-btn" className="inline-flex items-center gap-3 bg-gold text-maroon px-10 py-4 text-sm uppercase tracking-[0.18em] hover:bg-gold-hover transition-colors duration-300 shrink-0">
                Get in Touch <ArrowUpRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
